/**
 * AiAssistant — viewport-fixed FAB that expands into the streaming chat panel.
 *
 * Preserved from the original component:
 *  - isExpanded toggle, FAB appearance when collapsed, cubic-bezier transitions.
 *  - useOffline() + OFFLINE_HELPER_TEXT offline guard.
 *  - VoiceCommandButton / useVoiceRecording wiring (delegated to ChatPanel).
 *  - Viewport-fixed positioning and responsive sizing (isMobile / isTablet).
 *  - Snackbar / Alert for transient notifications.
 *
 * Changed:
 *  - zIndex bumped to 1100 (was 1000).
 *  - The single-shot POST /ai/command flow replaced by <ChatPanel />, which
 *    owns the SSE streaming connection via streamChat().
 *  - The Collapse body now renders ChatPanel instead of a plain form.
 *  - Panel height is taller to accommodate the message thread.
 */

import React, { useContext, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import { KeyboardArrowDown as MinimizeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOffline } from '../../offline';
import { AuthContext } from '../../auth/AuthContext';
import ChatPanel from './ChatPanel';
import { ASSISTANT_NAME, OFFLINE_HELPER_TEXT, ReinsightMark } from './ReinsightMark';

// Re-export RunningHorse so it stays discoverable at this path.
export { RunningHorse } from './RunningHorse';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AiAssistant: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
    invoiceData?: { invoiceId: string; invoiceNumber: string };
  }>({
    open: false,
    message: '',
    type: 'success',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));   // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600–900px

  const navigate = useNavigate();
  // Observed connectivity — defaults online when offline mode is off (no provider).
  const { isOnline } = useOffline();
  const { user } = useContext(AuthContext);

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  // ── Responsive sizing (preserved from original) ──

  const getExpandedWidth = () => {
    if (isMobile) return 'calc(100vw - 16px)';  // near-full-width on mobile
    if (isTablet) return '440px';
    return '500px';  // desktop: 460–520px range, using 500px
  };

  const getCollapsedSize = () => (isMobile ? 48 : 56);

  const getPosition = () =>
    isMobile ? { bottom: 12, right: 12 } : { bottom: 20, right: 20 };

  const position = getPosition();
  const collapsedSize = getCollapsedSize();

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: position.bottom,
        right: position.right,
        width: isExpanded ? getExpandedWidth() : collapsedSize,
        maxWidth: isExpanded
          ? { xs: 'calc(100vw - 16px)', sm: '440px', md: '500px' }
          : collapsedSize,
        height: isExpanded ? 'auto' : collapsedSize,
        // Bumped from 1000 → 1100 per handoff spec.
        zIndex: 1100,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transformOrigin: 'right bottom',
      }}
      className="ai-assistant"
    >
      <Paper
        elevation={3}
        sx={{
          p: isExpanded ? { xs: 1.5, sm: 2 } : 0,
          // Collapsed: the Reinsight icon is its own orange disc, so the Paper
          // stays transparent and only lends the shadow + hover scale.
          backgroundColor: isExpanded ? 'background.paper' : 'transparent',
          borderRadius: isExpanded ? 2 : '50%',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: isExpanded ? 'scale(1)' : 'scale(0.95)',
          border: isExpanded ? '1px solid #D1D5DB' : 'none',
          boxShadow: isExpanded
            ? '0 8px 30px rgba(0,0,0,0.12)'
            : '0 6px 24px rgba(139,94,52,0.45)',
          '&:hover': {
            transform: isExpanded ? 'scale(1)' : 'scale(1.05)',
            boxShadow: isExpanded
              ? '0 8px 30px rgba(0,0,0,0.12)'
              : '0 8px 32px rgba(139,94,52,0.55)',
          },
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // Collapsed: this box IS the button footprint — exactly collapsedSize,
            // no padding, the icon centred by flex. (The invisible title is taken
            // out of flow below so it cannot stretch the box past the Paper.)
            justifyContent: isExpanded ? 'space-between' : 'center',
            mb: isExpanded ? { xs: 0.5, sm: 1 } : 0,
            width: isExpanded ? 'auto' : collapsedSize,
            height: isExpanded ? 'auto' : collapsedSize,
            p: 0,
            position: 'relative',
            overflow: isExpanded ? 'visible' : 'hidden',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              // Out of flow while collapsed so it takes no space in the 56px box.
              position: isExpanded ? 'static' : 'absolute',
              pointerEvents: isExpanded ? 'auto' : 'none',
              opacity: isExpanded ? 1 : 0,
              visibility: isExpanded ? 'visible' : 'hidden',
              transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isExpanded ? 'translateY(0)' : 'translateY(10px)',
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            <ReinsightMark variant="mark" size={28} />
            {ASSISTANT_NAME}
          </Typography>
          <Tooltip title={isExpanded ? 'Minimize' : ASSISTANT_NAME}>
            <IconButton
              onClick={toggleExpanded}
              size="small"
              sx={{
                // Collapsed: the button is the whole footprint and the icon fills it.
                position: 'relative',
                flexShrink: 0,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                width: isExpanded ? { xs: '36px', sm: '32px' } : collapsedSize,
                height: isExpanded ? { xs: '36px', sm: '32px' } : collapsedSize,
                minWidth: isExpanded ? { xs: 44, sm: 32 } : collapsedSize,
                minHeight: isExpanded ? { xs: 44, sm: 32 } : collapsedSize,
                p: isExpanded ? undefined : 0,
                borderRadius: '50%',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: isExpanded ? 'action.hover' : 'transparent',
                },
              }}
            >
              {isExpanded ? (
                <MinimizeIcon />
              ) : (
                <ReinsightMark variant="icon" size={collapsedSize} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Expanded panel body ── */}
        <Collapse
          in={isExpanded}
          timeout={300}
          easing={{
            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
            exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Box
            sx={{
              opacity: isExpanded ? 1 : 0,
              transform: isExpanded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              visibility: isExpanded ? 'visible' : 'hidden',
            }}
          >
            {/* Sub-title / offline notice */}
            <Typography
              variant="body2"
              color={isOnline ? 'text.secondary' : 'warning.main'}
              gutterBottom
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                lineHeight: { xs: 1.3, sm: 1.43 },
                display: { xs: 'none', sm: 'block' },
                mb: 1,
              }}
            >
              {isOnline
                ? 'Ask anything about your clinic: appointments, patients, invoices, and more.'
                : OFFLINE_HELPER_TEXT}
            </Typography>

            {/* Chat conversation surface. A new one per signed-in person: a PIN unlock as someone
                else swaps the user with nothing unmounting, and the chat must not carry over. */}
            <ChatPanel key={user?.uid} />
          </Box>
        </Collapse>
      </Paper>

      {/* ── Transient notification Snackbar (preserved from original) ── */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.invoiceData ? 8000 : 4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: '100%' }}
          action={
            notification.invoiceData && (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  navigate('/billing', {
                    state: { invoiceId: notification.invoiceData?.invoiceId },
                  });
                  handleCloseNotification();
                }}
                sx={{ fontWeight: 'bold' }}
              >
                View Invoice
              </Button>
            )
          }
        >
          <Box>
            {notification.message}
            {notification.invoiceData && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Invoice #{notification.invoiceData.invoiceNumber}
              </Typography>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AiAssistant;
